import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import adminPC from "../assets/adminPC.png";
import adminXB from "../assets/adminXB.png";
import adminPS from "../assets/adminPS.png";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";


const AllAdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState(""); // Search by Email
  const [dateRange, setDateRange] = useState([null, null]); // Start and End Date
  const [startDate, endDate] = dateRange; // Destructure dateRange
  const [filterType, setFilterType] = useState(""); // Filter by Type
  const [filterStatus, setFilterStatus] = useState(""); // Filter by Status
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1); // Current page number
  const itemsPerPage = 30; // Orders per page


  // Fetch orders from the database
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get('/orders/allOrders', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setOrders(response.data);
      } catch (err) {
        setError("Failed to fetch orders. Please try again later.");
        console.error("Error fetching orders:", err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchOrders();
  }, [token]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/orders/allOrders`, {
          headers: {
            Authorization: `Bearer ${token}`, // Pass the token in headers
          },
        });
        setOrders(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [token]);


  // Calculate the total pages


  const handleStatusChange = async (orderId, newStatus) => {
    try {
      // Send the PUT request to the backend
      await api.put(`${process.env.REACT_APP_API_BASE_URL}/orders/${orderId}/status`, {
        status: newStatus,
      }, {
        headers: {
          Authorization: `Bearer ${token}`, // Include the auth token
        },
      });

      // Update the local state after the successful update
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      );
    } catch (err) {
      setError(err.message);
    }
  };


  // Filter orders based on search, date range, type, and status
  const filteredOrders = orders.filter((order) => {
    const matchesSearch = order.contactEmail
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesType = !filterType || order.type === filterType;
    const matchesStatus = !filterStatus || order.status === filterStatus;

    const orderDate = new Date(order.createdAt);
    const matchesDateRange =
      (!startDate || orderDate >= startDate) &&
      (!endDate || orderDate <= endDate);

    return matchesSearch && matchesType && matchesStatus && matchesDateRange;
  });

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  // Get the orders for the current page
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  const resetFilters = () => {
    setDateRange([null, null]);
    setFilterType("");
    setFilterStatus("");
  };
  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    } else if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="flex flex-col md:flex-row">
      {/* Sidebar */}
      <Sidebar />

      {/* Content */}
      <div className="flex-1 bg-gray-50 p-4 md:p-6">
        <h1 className="text-lg md:text-2xl font-bold text-gray-700">Order Lists</h1>

        {/* Search by Email */}
        <div className="flex flex-col md:flex-row items-start md:items-center mt-4 md:mt-6 mb-4 space-y-4 md:space-y-0 md:space-x-4">
          <input
            type="text"
            placeholder="Search by Email ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full md:w-1/3 focus:outline-none focus:ring focus:border-primary"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center space-y-4 md:space-y-0 md:space-x-4 mt-4 mb-4">
          {/* Date Range Picker */}
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            isClearable
            placeholderText="Select a date range"
            className="border rounded-lg px-4 py-2 w-full md:w-auto focus:outline-none focus:ring focus:border-primary"
          />

          {/* Filter by Type */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full md:w-auto focus:outline-none focus:ring focus:border-primary"
          >
            <option value="">All Consoles</option>
            <option value="XB">Xbox</option>
            <option value="PS">PlayStation</option>
            <option value="PC">PC</option>
          </select>

          {/* Filter by Status */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border rounded-lg px-4 py-2 w-full md:w-auto focus:outline-none focus:ring focus:border-primary"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="Completed">Completed</option>
          </select>

          {/* Reset Filters */}
          <button
            onClick={resetFilters}
            className="text-red-500 hover:text-red-700 font-medium w-full md:w-auto"
          >
            Reset Filters
          </button>
        </div>

        {/* Error Message */}
        {error && <p className="text-red-500">{error}</p>}

        {/* Loading State */}
        {loading ? (
          <p className="text-gray-600">Loading orders...</p>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-md">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-700">Order ID</th>
                    <th className="px-4 py-2 text-left text-gray-700">Contact Email</th>
                    <th className="px-4 py-2 text-left text-gray-700">Quantity</th>
                    <th className="px-4 py-2 text-left text-gray-700">Type</th>
                    <th className="px-4 py-2 text-left text-gray-700">Status</th>
                    <th className="px-4 py-2 text-left text-gray-700">Created At</th>
                    <th className="px-4 py-2 text-left text-gray-700">View Details</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((order) => (
                    <tr key={order._id} className="border-b">
                      <td className="px-4 py-2 text-gray-700">{order.orderId}</td>
                      <td className="px-4 py-2 text-gray-700">{order.contactEmail}</td>
                      <td className="px-4 py-2 text-gray-700">{order.quantity}</td>
                      <td className="px-4 py-2 text-gray-700">
                        {order.type === "XB" ? (
                          <img src={adminXB} alt="Xbox" className="w-6 h-6" />
                        ) : order.type === "PS" ? (
                          <img src={adminPS} alt="PlayStation" className="w-6 h-6" />
                        ) : (
                          <img src={adminPC} alt="PC" className="w-6 h-6" />
                        )}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <select
                          value={order.status}
                          onChange={(e) => handleStatusChange(order._id, e.target.value)}
                          className={`border p-1 rounded-lg text-white font-medium ${order.status === "pending"
                            ? "bg-purple-500 hover:bg-purple-600"
                            : "bg-green-500 hover:bg-green-600"
                            }`}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Completed">Completed</option>
                        </select>
                      </td>
                      <td className="px-4 py-2 text-gray-700">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => navigate(`/admin/order-info/${order._id}`)}
                          className="px-4 py-2 bg-primary text-black rounded-lg hover:bg-yellow-600"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="grid grid-cols-1 md:hidden gap-4">
              {filteredOrders.map((order) => (
                <div
                  key={order._id}
                  className="border rounded-lg shadow-md bg-white p-4 flex flex-col space-y-2"
                >
                  <div className="flex justify-between items-center">
                    {/* <span className="text-gray-700 font-bold text-lg">
                      {order.contactEmail}
                    </span> */}
                    {/* toggle pending */}
                    <div className="flex items-center space-x-2">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${order.status === "pending"
                          ? "bg-purple-200 text-purple-700"
                          : "bg-green-200 text-green-700"
                          }`}
                      >
                        {order.status}
                      </span>

                      {/* Toggle button */}
                      <button
                        value={order.status}
                        onClick={() => handleStatusChange(order._id, order.status === "pending" ? "completed" : "pending")}
                        className={`border p-1 rounded-lg text-white font-medium ${order.status === "pending"
                          ? "bg-purple-500 hover:bg-purple-600"
                          : "bg-green-500 hover:bg-green-600"
                          }`}
                      >
                        {/* Font Awesome icon for toggle */}
                        <i className={`fas ${order.status === "pending" ? "fa-toggle-on" : "fa-toggle-off"}`} />
                      </button>
                    </div>

                  </div>
                  <p className="text-gray-500 text-sm">ID: {order.orderId}</p>
                  <p className="text-gray-500 text-sm">QTY: {order.quantity}</p>
                  <p>
                    {order.type === "XB" ? (
                      <img src={adminXB} alt="Xbox" className="w-6 h-6" />
                    ) : order.type === "PS" ? (
                      <img src={adminPS} alt="PlayStation" className="w-6 h-6" />
                    ) : (
                      <img src={adminPC} alt="PC" className="w-6 h-6" />
                    )}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-bold">${order.priceInUSD}</span>

                    <button
                      onClick={() => navigate(`/admin/order-info/${order._id}`)}
                      className="px-4 py-2 bg-primary text-black rounded-lg hover:bg-yellow-600"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-center mt-4 space-x-4">
          <button
            onClick={() => handlePageChange("prev")}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
          >
            Previous
          </button>

          <span className="text-gray-700">
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => handlePageChange("next")}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );

};

export default AllAdminOrders;
